import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaChevronLeft, FaChevronRight, FaDownload, FaEnvelope, FaTimes } from 'react-icons/fa';
import ProductDownloadModal from './ProductDownloadModal';
import ProductEnquiryModal from './ProductEnquiryModal';
import API_URL, { apiPath } from '../../config/api';
import { buildAbsoluteUrl, useSeo } from '../../utils/seo';
import { buildProductPath, isLegacyProductId } from '../../utils/productRoutes';
import { getRichTextPlainText, sanitizeRichTextHtml } from '../../utils/richText';
import { getPrimaryProductImage, getProductGalleryImages } from '../../utils/productMedia';
import pic1 from '../../Assets/pic1.jpg';
import './ProductDetail.css';

const fallbackImage = pic1;

const formatLabel = (value = '') =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(' ');

const parseFeatureItems = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue.filter(Boolean) : [value];
    } catch {
      return value
        .split('\n')
        .map((feature) => feature.trim())
        .filter(Boolean);
    }
  }

  return [];
};

const parseSpecificationItems = (value) => {
  if (Array.isArray(value)) {
    return value.filter(
      (specification) =>
        specification && (specification.name || specification.label || specification.value)
    );
  }

  if (typeof value === 'string' && value.trim()) {
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [];
    } catch {
      return [];
    }
  }

  return [];
};

const ProductDetail = () => {
  const { productSlug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);

  useEffect(() => {
    fetchProductDetails();
    fetchAllProducts();
  }, [productSlug]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      setProduct(null);

      if (isLegacyProductId(productSlug)) {
        const redirectResponse = await fetch(
          `${apiPath('products', 'redirect', productSlug)}?format=json`
        );

        const redirectResult = await redirectResponse.json();

        if (redirectResponse.ok && redirectResult.success && redirectResult.redirect_to) {
          navigate(redirectResult.redirect_to, { replace: true });
          return;
        }

        throw new Error(redirectResult.message || 'Product not found');
      }

      const response = await fetch(apiPath('products', 'slug', productSlug));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setProduct(result.product || result.data);
      } else {
        setError(result.message || 'Product not found');
      }
    } catch (fetchError) {
      console.error('Error fetching product:', fetchError);
      setError(`Failed to fetch product details: ${fetchError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllProducts = async () => {
    try {
      const response = await fetch(API_URL.PRODUCTS);
      const result = await response.json();

      if (response.ok && result.success) {
        setAllProducts(
          Array.isArray(result.products)
            ? result.products
            : Array.isArray(result.data)
              ? result.data
              : []
        );
      }
    } catch (fetchError) {
      console.error('Error fetching similar products:', fetchError);
    }
  };

  useEffect(() => {
    setSelectedImageIndex(0);
  }, [productSlug, product?.id, product?._id, product?.slug]);

  const galleryImages = product ? getProductGalleryImages(product) : [];
  const featureItems = parseFeatureItems(product?.features);
  const specificationItems = parseSpecificationItems(product?.specifications);
  const descriptionHtml = sanitizeRichTextHtml(product?.description || '');
  const descriptionText =
    getRichTextPlainText(product?.description || '') ||
    featureItems.map((feature) => getRichTextPlainText(feature || '')).join(' ');
  const productNameText = getRichTextPlainText(product?.name || '') || 'Product';
  const categoryLabel = formatLabel(product?.category || '');
  const subcategoryLabel = formatLabel(product?.subcategory || '');
  const resolvedProductPath = buildProductPath(product || productSlug);
  const seoImage = galleryImages[0]?.url || getPrimaryProductImage(product, fallbackImage) || fallbackImage;

  useSeo({
    enabled: Boolean(product),
    title: productNameText,
    description: descriptionText || `${productNameText} by Hari Krishna Industries.`,
    pathname: resolvedProductPath,
    image: /^https?:\/\//i.test(seoImage)
      ? seoImage.replace(/^https?:\/\/[^/]+/i, '')
      : seoImage,
    imageAlt: productNameText,
    type: 'product',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Products', path: '/products' },
      ...(categoryLabel
        ? [
            {
              name: categoryLabel,
              path: `/products?category=${encodeURIComponent(categoryLabel)}`,
            },
          ]
        : []),
      { name: productNameText, path: resolvedProductPath },
    ],
    structuredData: product
      ? [
          {
            '@type': 'Product',
            name: productNameText,
            description: descriptionText || `${productNameText} by Hari Krishna Industries.`,
            url: buildAbsoluteUrl(resolvedProductPath),
            image: galleryImages.map((image) => image.url).filter(Boolean).slice(0, 8),
            category: subcategoryLabel || categoryLabel || undefined,
            sku: product.sku || undefined,
            brand: {
              '@type': 'Brand',
              name: 'Hari Krishna Industries',
            },
          },
        ]
      : [],
  });

  const handleDownloadSpecs = () => {
    setShowDownloadModal(true);
  };

  const handleEnquireNow = () => {
    setShowEnquiryModal(true);
  };

  const handleDownloadSuccess = () => {};

  const handleEnquirySuccess = () => {};

  if (loading) {
    return (
      <div className="product-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading product details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="product-detail-error">
        <div className="error-content">
          <FaTimes className="error-icon" />
          <h2>Product Not Found</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="back-btn">
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const mainImage =
    galleryImages[selectedImageIndex]?.url ||
    getPrimaryProductImage(product, fallbackImage) ||
    fallbackImage;
  const showGalleryControls = galleryImages.length > 1;

  const handlePreviousImage = () => {
    setSelectedImageIndex((previousIndex) =>
      previousIndex <= 0 ? galleryImages.length - 1 : previousIndex - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((previousIndex) =>
      previousIndex >= galleryImages.length - 1 ? 0 : previousIndex + 1
    );
  };

  const similarProductsBase = allProducts
    .filter((item) => String(item._id ?? item.id) !== String(product._id ?? product.id))
    .sort((left, right) => {
      const leftScore =
        String(left.subcategory || '').toLowerCase() === String(product.subcategory || '').toLowerCase()
          ? 2
          : String(left.category || '').toLowerCase() === String(product.category || '').toLowerCase()
            ? 1
            : 0;
      const rightScore =
        String(right.subcategory || '').toLowerCase() === String(product.subcategory || '').toLowerCase()
          ? 2
          : String(right.category || '').toLowerCase() === String(product.category || '').toLowerCase()
            ? 1
            : 0;

      if (rightScore !== leftScore) {
        return rightScore - leftScore;
      }

      return String(left.name || '').localeCompare(String(right.name || ''));
    });

  const sameCategoryProducts = similarProductsBase.filter(
    (item) => String(item.category || '').toLowerCase() === String(product.category || '').toLowerCase()
  );
  const similarProducts =
    (sameCategoryProducts.length > 0 ? sameCategoryProducts : similarProductsBase).slice(0, 4);

  const navigateToProduct = (targetProduct) => {
    navigate(buildProductPath(targetProduct));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="product-detail-page product-detail-zebco">
      <div className="product-detail-container">
        <div className="product-detail-breadcrumb">
          <button type="button" className="breadcrumb-home-link" onClick={() => navigate('/')}>
            Home
          </button>
          {categoryLabel && (
            <>
              <span className="breadcrumb-sep">/</span>
              <button
                type="button"
                className="breadcrumb-home-link"
                onClick={() => navigate(`/products?category=${encodeURIComponent(categoryLabel)}`)}
              >
                {categoryLabel}
              </button>
            </>
          )}
          {subcategoryLabel && (
            <>
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-item">{subcategoryLabel}</span>
            </>
          )}
        </div>

        <motion.div
          className="product-detail-main zebco-layout"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="zebco-detail-header-row">
            <div className="zebco-header-topline">
              <h2 className="zebco-heading">PRODUCT DESCRIPTION</h2>
              <div className="zebco-header-actions">
                <button className="zebco-btn zebco-btn-primary" onClick={handleEnquireNow}>
                  <FaEnvelope /> Inquiry
                </button>
                <button className="zebco-btn zebco-btn-outline" onClick={handleDownloadSpecs}>
                  <FaDownload /> Download Catalog
                </button>
              </div>
            </div>
            <h1 className="product-detail-product-heading zebco-product-name">
              {productNameText}
            </h1>
          </div>

          <div className="product-details-section zebco-details">
            <div className="zebco-details-inner">
              <div className="zebco-specs-card">
                <div className="zebco-description-block">
                  <strong>Description</strong>
                  <div
                    className="zebco-description-body"
                    dangerouslySetInnerHTML={{
                      __html:
                        descriptionHtml ||
                        '<p>Detailed product information will be added soon.</p>',
                    }}
                  />
                </div>

                {featureItems.length > 0 && (
                  <div className="zebco-features-block">
                    <strong>Features</strong>
                    <ul className="product-point-list">
                      {featureItems.map((feature, index) => (
                        <li key={`${product._id ?? product.id}-feature-${index}`}>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: sanitizeRichTextHtml(feature, { wrapPlainText: false }),
                            }}
                          />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {specificationItems.length > 0 && (
                  <>
                    <div className="zebco-features-block">
                      <strong>Specifications</strong>
                    </div>
                    <dl className="zebco-specs-list">
                      {specificationItems.map((specification, index) => (
                        <div
                          key={`${product._id ?? product.id}-spec-${index}`}
                          className="zebco-spec-row"
                        >
                          <dt>{specification.name || specification.label || 'Specification'}</dt>
                          <dd>{specification.value || specification.text || '-'}</dd>
                        </div>
                      ))}
                    </dl>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="product-image-section zebco-images">
            <div className="image-gallery zebco-style">
              <div className="main-display-area">
                <div className="main-image-container">
                  {showGalleryControls && (
                    <>
                      <button
                        type="button"
                        className="image-nav-btn image-prev"
                        onClick={handlePreviousImage}
                        aria-label="Previous image"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        type="button"
                        className="image-nav-btn image-next"
                        onClick={handleNextImage}
                        aria-label="Next image"
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}

                  <img
                    src={mainImage}
                    alt={productNameText}
                    className="main-image"
                    onError={(event) => {
                      event.target.src = fallbackImage;
                    }}
                  />
                </div>
              </div>

              {galleryImages.length > 1 && (
                <div className="thumbnail-strip-right">
                  {galleryImages.map((image, index) => (
                    <button
                      key={image.id || `product-image-${index}`}
                      type="button"
                      className={`thumbnail${selectedImageIndex === index ? ' active' : ''}`}
                      onClick={() => setSelectedImageIndex(index)}
                      aria-label={`Show image ${index + 1}`}
                    >
                      <img
                        src={image.url}
                        alt={`${productNameText} thumbnail ${index + 1}`}
                        onError={(event) => {
                          event.target.src = fallbackImage;
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {similarProducts.length > 0 && (
          <section className="similar-products-section">
            <h2 className="similar-products-title zebco-section-title">Similar Products</h2>
            <div className="similar-products-grid">
              {similarProducts.map((similarProduct, index) => {
                const similarProductId = similarProduct._id ?? similarProduct.id;
                const similarProductName =
                  getRichTextPlainText(similarProduct.name || '') || 'Product';

                return (
                  <div
                    key={similarProductId || `similar-product-${index}`}
                    className="similar-product-card"
                    onClick={() => navigateToProduct(similarProduct)}
                  >
                    <div className="similar-product-image">
                      <img
                        src={getPrimaryProductImage(
                          similarProduct,
                          fallbackImage
                        )}
                        alt={similarProductName}
                        onError={(event) => {
                          event.target.src = fallbackImage;
                        }}
                      />
                    </div>
                    <div className="similar-product-name">{similarProductName}</div>
                    <div className="similar-product-category">
                      {formatLabel(similarProduct.subcategory || similarProduct.category || '')}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <ProductDownloadModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        product={product}
        onSuccess={handleDownloadSuccess}
      />

      <ProductEnquiryModal
        isOpen={showEnquiryModal}
        onClose={() => setShowEnquiryModal(false)}
        product={product}
        onSuccess={handleEnquirySuccess}
      />
    </div>
  );
};

export default ProductDetail;
